import { Component } from '@theme/component';
import { sectionRenderer } from '@theme/section-renderer';
import { StandardEvents } from '@shopify/events';

/**
 * Schnellfilter-Chipleiste der Gazelle-LP (sections/ebo-gaz-liste-kopf.liquid).
 *
 * Die Chips sind serverseitig gerenderte Facetten-Links (url_to_add /
 * url_to_remove). Ohne JS navigieren sie ganz normal (Fallback inkl.
 * #ResultsList-Anker auf die Produktliste). Mit JS werden Klicks in den
 * nativen Facets-Flow des Themes umgeleitet:
 * facets-form-component.updateFiltersByURL() pusht die URL und morpht die
 * Produktliste per Section-Rendering-API — dasselbe Muster wie die aktiven
 * Filter-Pills (facet-remove-component in assets/facets.js), ohne Reload und
 * ohne Scroll-Sprung.
 *
 * Da Aktiv-Markierung und URLs der Chips serverseitig aus collection.filters
 * berechnet werden, rendert die Leiste ihre eigene Section nach jedem
 * Filter-Update neu (collection:update — egal ob per Chip oder per nativer
 * Filterleiste ausgelöst), damit Markierung und Links aktuell bleiben.
 *
 * @extends {Component}
 */
class EboGazChipsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(StandardEvents.collectionUpdate, this.#leisteAktualisieren);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(StandardEvents.collectionUpdate, this.#leisteAktualisieren);
  }

  /**
   * Wendet die Facetten-URL eines Chips ohne Seiten-Reload an (on:click).
   * @param {MouseEvent} event - Das Klick-Event
   */
  chipAnwenden(event) {
    // Neuer Tab / neues Fenster (Modifier- oder Nicht-Primärklick) dem Browser überlassen
    if (event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
    if (event instanceof MouseEvent && event.button !== 0) return;

    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;

    const facetsForm = document.querySelector('facets-form-component');
    // Upgrade-Race schließen: Element kann im DOM sein, bevor facets.js
    // dessen Klasse registriert hat (vgl. registerEventListeners in component.js)
    if (facetsForm instanceof HTMLElement) customElements.upgrade(facetsForm);

    // Kein natives Facets-Formular (Filterleiste entfernt oder facets.js noch
    // nicht geladen): normale Navigation als Fallback zulassen
    const updateFiltersByURL = /** @type {any} */ (facetsForm)?.updateFiltersByURL;
    if (typeof updateFiltersByURL !== 'function') return;

    event.preventDefault();

    // #ResultsList-Anker (No-JS-Fallback) nicht in die History übernehmen
    const url = new URL(link.href, window.location.origin);
    updateFiltersByURL.call(facetsForm, url.pathname + url.search);
  }

  /**
   * Rendert die eigene Section nach jedem Filter-Update neu, damit
   * Aktiv-Markierung und Chip-URLs zum neuen Filterstand passen.
   */
  #leisteAktualisieren = () => {
    const sectionId = this.closest('.shopify-section')?.id;
    if (sectionId) sectionRenderer.renderSection(sectionId);
  };
}

if (!customElements.get('ebo-gaz-chips-component')) {
  customElements.define('ebo-gaz-chips-component', EboGazChipsComponent);
}
